/**
 * 应用级数据状态。
 *
 * 刻意不引 Redux / Zustand：这个应用的数据是"服务端为准的少量集合"
 * （冰箱、清单、偏好、能力），一个 Context + 若干 refresh 函数足够，
 * 也更好读。需要局部状态的页面自己用 useState。
 *
 * 三个特殊状态值得说明，它们的处置完全不同，别混在一起：
 *   · needsOnboarding —— 这台设备还没加入任何冰箱 → 引导页（创建 / 输邀请码）
 *   · authRequired    —— 服务端开了站点口令而本机没填 → 口令页
 *   · error           —— 网络或服务挂了 → 页面内提示 + 重试
 *
 * 还有一个"自愈"逻辑：如果本机存着的某把钥匙被服务端拒了（比如被别的
 * 设备踢了、或者这个冰箱被删了），就把它从本机列表里划掉，然后自动
 * 试下一个冰箱。用户不需要理解"钥匙失效"这件事，他只会看到自己
 * 回到了另一个能用的冰箱，或者引导页。
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { MealBrief, Recipe } from '@fridge/shared';

import { useToast } from '../components/Toast';

import {
  ApiError,
  fetchBuyList,
  fetchFridge,
  fetchHouseholdDetail,
  fetchIntakeCapabilities,
  fetchOnboarding,
  fetchOverview,
  fetchPrefs,
  forgetHousehold,
  generateRecipes,
  getActiveHousehold,
  getActiveHouseholdId,
  HouseholdRequiredError,
  listStoredHouseholds,
  rememberHousehold,
  renameStoredHousehold,
  setActiveHousehold,
  UnauthError,
  updateStoredHouseholdMeta,
  type BuyListResponse,
  type FridgeResponse,
  type HouseholdDetail,
  type HouseholdSession,
  type IntakeCapabilities,
  type OnboardingInfo,
  type Overview,
  type PrefsResponse,
  type StoredHousehold,
} from '../api/client';

/**
 * 生成菜谱的结果放在应用级，而不是做菜页的局部状态。
 *
 * 原因是实测出来的：页面用 `key={route}` 强制重挂载，一切换标签页，
 * 做菜页的 state 全没了 —— 而那笔请求还在飞。用户点完「给我几个菜」
 * 去冰箱页看两眼再回来，看到的是空白的"还没生成过菜谱"，
 * 结果其实已经拿到、也已经存进历史了，只是没人接住它。
 *
 * 放在这一层，请求的回调闭包活到 provider 卸载为止，切页面不会丢；
 * 底部导航还能顺手显示"正在生成"。代价只是多三个 state。
 */
export interface GenerationState {
  /** 正在生成 */
  busy: boolean;
  /** 最近一次生成的结果，做菜页的"今天"这一栏显示的就是它 */
  recipes: Recipe[];
  /** 解释"为什么给我推这几道" */
  contextSummary: string | null;
}

interface AppDataValue {
  caps: IntakeCapabilities | null;
  fridge: FridgeResponse | null;
  buyList: BuyListResponse | null;
  prefs: PrefsResponse | null;
  overview: Overview | null;
  /** 当前冰箱的元信息（名字、成员数、邀请码） */
  detail: HouseholdDetail | null;
  loading: boolean;

  /** 首次加载失败的原因（网络问题 / 服务没起） */
  error: string | null;
  /** 需要用户填站点口令 */
  authRequired: boolean;
  /** 需要用户创建或加入一个冰箱 */
  needsOnboarding: boolean;
  /** 服务端的引导信息（要不要口令、名字长度上限） */
  onboarding: OnboardingInfo | null;

  /** 本机记住的全部冰箱，用于左上角切换器 */
  households: StoredHousehold[];
  active: StoredHousehold | null;

  refreshAll: () => Promise<void>;
  refreshFridge: () => Promise<void>;
  refreshBuyList: () => Promise<void>;
  refreshPrefs: () => Promise<void>;
  refreshDetail: () => Promise<void>;

  /**
   * 菜谱生成的状态与入口。
   *
   * 放在这里而不是做菜页里，是为了让"点完就走"成立：请求发出去之后
   * 用户可以切去冰箱页、待买页，回来结果还在。
   */
  generation: GenerationState;
  generate: (brief: MealBrief, count?: number) => Promise<void>;
  /** 收藏 / 删除之后同步"今天"这一栏（列表在这个 provider 里） */
  replaceTodayRecipes: (list: Recipe[]) => void;

  /** 创建 / 加入成功之后调用：记住这个冰箱并加载它的数据 */
  completeOnboarding: (session: HouseholdSession) => Promise<void>;
  /** 切换当前冰箱 */
  switchHousehold: (id: string) => Promise<void>;
  /** 退出当前冰箱（从本机忘掉；服务端那把钥匙由设置页负责踢） */
  forgetCurrent: () => Promise<void>;
  /** 改名之后同步本地缓存的名字，否则切换器里还是旧名字 */
  applyLocalRename: (id: string, name: string) => void;
}

const AppDataContext = createContext<AppDataValue | null>(null);

export function AppDataProvider({ children }: { children: ReactNode }): JSX.Element {
  const [caps, setCaps] = useState<IntakeCapabilities | null>(null);
  const [fridge, setFridge] = useState<FridgeResponse | null>(null);
  const [buyList, setBuyList] = useState<BuyListResponse | null>(null);
  const [prefs, setPrefs] = useState<PrefsResponse | null>(null);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [detail, setDetail] = useState<HouseholdDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [onboarding, setOnboarding] = useState<OnboardingInfo | null>(null);

  const [households, setHouseholds] = useState<StoredHousehold[]>(() => listStoredHouseholds());
  const [active, setActive] = useState<StoredHousehold | null>(() => getActiveHousehold());

  const [generating, setGenerating] = useState(false);
  const [todayRecipes, setTodayRecipes] = useState<Recipe[]>([]);
  const [todaySummary, setTodaySummary] = useState<string | null>(null);
  /** 同一个请求还没回来时，再点「给我几个菜」不该发第二次 */
  const generatingRef = useRef(false);
  // 只要 show，它的 identity 是稳定的（useCallback），放进依赖数组不会churn
  const { show: showToast } = useToast();

  // 防止并发 refresh 互相覆盖（下拉刷新 + 切换冰箱可能撞在一起）
  const refreshSeq = useRef(0);
  // 当前正在加载哪个冰箱。loadTenant 回调是稳定的（deps 为空），
  // 不能靠闭包读 active，否则拿到的可能是上一轮的值。
  const activeIdRef = useRef<string | null>(getActiveHouseholdId());

  const syncLocal = useCallback(() => {
    setHouseholds(listStoredHouseholds());
    setActive(getActiveHousehold());
  }, []);

  const clearTenantData = useCallback(() => {
    setFridge(null);
    setBuyList(null);
    setPrefs(null);
    setOverview(null);
    setDetail(null);
    // 换冰箱 / 退出之后，"今天"这一栏必须清掉：那是上一家的菜
    setTodayRecipes([]);
    setTodaySummary(null);
  }, []);

  const refreshFridge = useCallback(async () => {
    const data = await fetchFridge();
    setFridge(data);
    /**
     * 顺手把概览里的食材数对齐。
     *
     * 录入、吃完、入库这些动作都只调 refreshFridge()，不会重新拉概览 ——
     * 不管它的话，设置页那句"现在有 N 样"就永远停在进站那一刻的数字，
     * 而用户刚刚明明看见自己录了一样东西。fetchFridge 的 total 就是权威值，
     * 拿它覆盖掉，比为一个数字多发一次请求划算。
     */
    setOverview((previous) =>
      previous
        ? { ...previous, counts: { ...previous.counts, fridgeItems: data.total } }
        : previous,
    );
  }, []);

  const refreshBuyList = useCallback(async () => {
    setBuyList(await fetchBuyList());
  }, []);

  const refreshPrefs = useCallback(async () => {
    setPrefs(await fetchPrefs());
  }, []);

  const refreshDetail = useCallback(async () => {
    setDetail(await fetchHouseholdDetail());
  }, []);

  /**
   * 生成菜谱。
   *
   * 这个函数住在 provider 里，所以它的回调闭包不随页面卸载而消失 ——
   * 用户点完「给我几个菜」可以立刻切去冰箱页、待买页，回来时结果已经在了。
   * 放在做菜页里就做不到这件事：路由用 `key={route}` 强制重挂载，
   * 一切走 state 就没了，而请求还在飞。
   *
   * 两个边界都想过：
   *   · 生成期间切了冰箱 —— 结果属于上一家，丢掉不显示（服务端已经存好了，
   *     它会在那一家的历史里等着）。这时候显示出来才是错的。
   *   · 网络断 / 请求超时 —— 东西不会白做。服务端会把活干完并入库
   *     （实测过：客户端断开后 25 秒，3 道菜照样躺在历史里），
   *     这里只负责把失败说清楚。
   */
  const generate = useCallback(
    async (brief: MealBrief, count = 3) => {
      if (generatingRef.current) return;
      generatingRef.current = true;
      setGenerating(true);
      const startedFor = activeIdRef.current;

      try {
        const result = await generateRecipes(brief, count);
        if (activeIdRef.current !== startedFor) return;

        setTodayRecipes(result.recipes);
        setTodaySummary(result.contextSummary);

        if (result.recipes.length === 0) {
          showToast('这次没想出合适的菜，换个口味再试试。', 'warn');
        } else {
          const parts = [`${result.recipes.length} 道菜好了`];
          if (result.buyItemsAdded > 0) parts.push(`缺的 ${result.buyItemsAdded} 样已进待买`);
          if (result.rejected.length > 0) parts.push(`拦下 ${result.rejected.length} 道违规的`);
          showToast(parts.join('，'), 'success');
        }

        await refreshBuyList();
      } catch (err) {
        if (activeIdRef.current !== startedFor) return;
        showToast(err instanceof ApiError ? err.message : '生成失败', 'error');
      } finally {
        generatingRef.current = false;
        setGenerating(false);
      }
    },
    [refreshBuyList, showToast],
  );

  const replaceTodayRecipes = useCallback((list: Recipe[]) => {
    setTodayRecipes(list);
  }, []);

  /** 一次性把这个冰箱的全套数据取回来 */
  const loadTenant = useCallback(async () => {
    const [view, fridgeData, buyData, prefsData, detailData] = await Promise.all([
      fetchOverview(),
      fetchFridge(),
      fetchBuyList(),
      fetchPrefs(),
      fetchHouseholdDetail(),
    ]);
    setOverview(view);
    setFridge(fridgeData);
    setBuyList(buyData);
    setPrefs(prefsData);
    setDetail(detailData);
    /**
     * 顺手把服务端才知道的元信息写回本地缓存：冰箱可能被别人改了名、
     * 或者刚多了台设备。这样下次打开切换器，那些"没进去过"的冰箱
     * 也能显示成员数，而不是一句干巴巴的"切换过去看内容"。
     */
    updateStoredHouseholdMeta(activeIdRef.current ?? detailData.household.id, {
      name: detailData.household.name,
      memberCount: detailData.household.memberCount,
    });
  }, []);

  const refreshAll = useCallback(async () => {
    const seq = (refreshSeq.current += 1);
    /** 慢的请求回来时如果已经发起了新的一轮，就别再写状态了 */
    const isStale = (): boolean => seq !== refreshSeq.current;

    setLoading(true);
    try {
      // 这两个是免钥匙的，先拿它们把"要不要口令"问清楚
      const [capabilities, onboardingInfo] = await Promise.all([
        fetchIntakeCapabilities().catch(() => null),
        fetchOnboarding().catch(() => null),
      ]);
      if (isStale()) return;
      if (capabilities) setCaps(capabilities);
      if (onboardingInfo) setOnboarding(onboardingInfo);

      /**
       * 依次尝试本机记住的每一个冰箱。
       *
       * **必须从当前这个开始试**。如果无脑按列表顺序来，那么刚刚
       * "新建一个冰箱"或"用邀请码加入"之后紧接着的这次刷新，会把
       * 用户又切回列表里的第一个冰箱 —— 表现就是"我明明建好了，
       * 怎么还在原来那个里面"。这是个真踩过的坑，不是假想。
       *
       * 被服务端拒绝的（钥匙失效）就地划掉，本机列表会自己收敛到
       * "真正还能进的冰箱"。这里刻意不先写 active，等某个候选真的
       * 拉回数据了才切过去 —— 否则中途失败会留下一个指向死冰箱的当前项。
       */
      const candidates = listStoredHouseholds();
      if (candidates.length === 0) {
        clearTenantData();
        setNeedsOnboarding(true);
        setAuthRequired(false);
        setError(null);
        return;
      }

      const preferredId = getActiveHouseholdId() ?? active?.id ?? candidates[0]?.id;
      const ordered = [
        ...candidates.filter((h) => h.id === preferredId),
        ...candidates.filter((h) => h.id !== preferredId),
      ];

      for (const candidate of ordered) {
        if (isStale()) return;
        try {
          setActiveHousehold(candidate.id);
          activeIdRef.current = candidate.id;
          await loadTenant();
          if (isStale()) return;
          syncLocal();
          setNeedsOnboarding(false);
          setAuthRequired(false);
          setError(null);
          return;
        } catch (err) {
          if (err instanceof HouseholdRequiredError) {
            // 这把钥匙死了 —— 从本机划掉，试下一个
            forgetHousehold(candidate.id);
            syncLocal();
            continue;
          }
          throw err;
        }
      }

      // 每个都不行了
      clearTenantData();
      syncLocal();
      setNeedsOnboarding(true);
      setError(null);
      setAuthRequired(false);
    } catch (err) {
      if (isStale()) return;
      if (err instanceof UnauthError) {
        // 口令不对：先解决口令，别急着说"没有冰箱"
        setAuthRequired(true);
        setError(null);
        return;
      }
      if (err instanceof HouseholdRequiredError) {
        clearTenantData();
        syncLocal();
        setNeedsOnboarding(true);
        setError(null);
        return;
      }
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [clearTenantData, loadTenant, syncLocal]);

  const completeOnboarding = useCallback(
    async (session: HouseholdSession) => {
      rememberHousehold(session);
      syncLocal();
      await refreshAll();
    },
    [refreshAll, syncLocal],
  );

  const switchHousehold = useCallback(
    async (id: string) => {
      setActiveHousehold(id);
      syncLocal();
      // 切换之后先清空旧冰箱的数据，避免新数据回来之前界面上还挂着上一个冰箱的东西
      clearTenantData();
      await refreshAll();
    },
    [clearTenantData, refreshAll, syncLocal],
  );

  const forgetCurrent = useCallback(async () => {
    const current = getActiveHousehold();
    if (current) forgetHousehold(current.id);
    syncLocal();
    clearTenantData();
    await refreshAll();
  }, [clearTenantData, refreshAll, syncLocal]);

  const applyLocalRename = useCallback(
    (id: string, name: string) => {
      renameStoredHousehold(id, name);
      syncLocal();
    },
    [syncLocal],
  );

  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  const generation = useMemo<GenerationState>(
    () => ({ busy: generating, recipes: todayRecipes, contextSummary: todaySummary }),
    [generating, todayRecipes, todaySummary],
  );

  const value = useMemo<AppDataValue>(
    () => ({
      caps,
      fridge,
      buyList,
      prefs,
      overview,
      detail,
      loading,
      error,
      authRequired,
      needsOnboarding,
      onboarding,
      households,
      active,
      refreshAll,
      refreshFridge,
      refreshBuyList,
      refreshPrefs,
      refreshDetail,
      generation,
      generate,
      replaceTodayRecipes,
      completeOnboarding,
      switchHousehold,
      forgetCurrent,
      applyLocalRename,
    }),
    [
      caps,
      fridge,
      buyList,
      prefs,
      overview,
      detail,
      loading,
      error,
      authRequired,
      needsOnboarding,
      onboarding,
      households,
      active,
      refreshAll,
      refreshFridge,
      refreshBuyList,
      refreshPrefs,
      refreshDetail,
      generating,
      todayRecipes,
      todaySummary,
      generate,
      replaceTodayRecipes,
      completeOnboarding,
      switchHousehold,
      forgetCurrent,
      applyLocalRename,
    ],
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData(): AppDataValue {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData 必须在 AppDataProvider 内使用');
  return ctx;
}
